import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, addDoc, updateDoc, doc, query, where, getDocs, Timestamp, getDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { DndContext, DragOverlay, useSensor, useSensors, MouseSensor, TouchSensor, useDroppable, closestCorners } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, Clock, Calendar, User, ChevronLeft, ChevronRight, Plus, Copy, Search, RotateCcw, AlertTriangle, Check } from 'lucide-react';
import { db as firebaseDb } from '../firebase';
import ClientEditAppointmentModal from './ClientEditAppointmentModal';
import ClientAddAppointmentModal from './ClientAddAppointmentModal';
import RecurrenceEditModal from './RecurrenceEditModal';
import RecurrenceMoveModal from './RecurrenceMoveModal';
import AppointmentViewModal from './AppointmentViewModal';
import { generateRecurringAppointments } from '../utils/recurrenceUtils';


// Ajoutez cette fonction d'aide en haut de votre fichier
const isSameDay = (date1, date2) => {
  return date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear();
};
const ClientMonthView = ({
  currentDate,
  appointments,
  employees,
  db,
  setAppointments,
  user,
  client,
  refreshTrigger,
  onDateClick,
  hasPermission,
  fetchAppointments: propFetchAppointments
}) => {
  // Sécuriser l'instance Firestore
  const safeDb = db || firebaseDb;

  const [internalAppointments, setInternalAppointments] = useState([]);
  const [internalEmployees, setInternalEmployees] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);

  // Utiliser les props ou l'état interne
  const effectiveAppointments = appointments || internalAppointments;
  const effectiveEmployees = employees || internalEmployees;
  const setEffectiveAppointments = setAppointments || setInternalAppointments;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [leaves, setLeaves] = useState([]);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [pendingAppointmentUpdate, setPendingAppointmentUpdate] = useState(null);
  const [pendingMoveData, setPendingMoveData] = useState(null);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [maxVisibleAppointments] = useState(4);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [clickTimeout, setClickTimeout] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const copyModeRef = useRef(false);

  const canEditAppointments = useMemo(() => {
    if (typeof hasPermission === 'function') {
      return hasPermission({ resource: 'appointments', action: 'write' });
    }
    return true; // Valeur par défaut si non spécifié
  }, [hasPermission]);

  // Fonction pour basculer l'expansion d'un jour
  const toggleDayExpansion = useCallback((date) => {
    const dateKey = date.getTime().toString();
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const fetchLeaves = async () => {
      if (!safeDb) return;
      try {
        const leavesQuery = query(collection(safeDb, 'leaves'));
        const querySnapshot = await getDocs(leavesQuery);
        const leavesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setLeaves(leavesData);
      } catch (error) {
        console.error("Erreur lors de la récupération des congés:", error);
      }
    };

    fetchLeaves();
  }, [safeDb, refreshTrigger]);

  // Charger les rendez-vous si non fournis par props
  const fetchAppointments = useCallback(async () => {
    if (!safeDb || !client?.id || appointments) return;

    setAppointmentsLoading(true);
    try {
      const q = query(
        collection(safeDb, 'appointments'),
        where('clientId', '==', client.id)
      );
      const querySnapshot = await getDocs(q);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date()
      }));
      setInternalAppointments(fetchedAppointments);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous:", error);
    } finally {
      setAppointmentsLoading(false);
    }
  }, [safeDb, client?.id, appointments]);

  // Charger les employés si non fournis par props
  const fetchEmployees = useCallback(async () => {
    if (!safeDb || employees) return;

    try {
      const querySnapshot = await getDocs(collection(safeDb, 'employees'));
      const fetchedEmployees = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInternalEmployees(fetchedEmployees);
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [safeDb, employees]);

  useEffect(() => {
    fetchAppointments();
    fetchEmployees();
  }, [fetchAppointments, fetchEmployees, refreshTrigger]);

  // Gestion de la touche Ctrl pour le mode copie
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        setIsCopyMode(true);
        copyModeRef.current = true;
      }
    };

    const handleKeyUp = (e) => {
      if (!e.ctrlKey && !e.metaKey) {
        setIsCopyMode(false);
        copyModeRef.current = false;
      }
    };

    const handleBlur = () => {
      setIsCopyMode(false);
      copyModeRef.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const internalFetchAppointments = propFetchAppointments || fetchAppointments;

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: days }, (_, i) => new Date(year, month, i + 1));
  };

  const getDayNames = () => ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const monthShortNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

  const getMonthDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of the month
    const firstDayOfMonth = new Date(year, month, 1);
    // Last day of the month
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Days in current month
    const daysInMonth = getDaysInMonth(currentDate);

    // Calculate padding days for start (previous month)
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 (Sun) to 6 (Sat)
    const paddingDaysBefore = [];
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      paddingDaysBefore.push(date);
    }
    // If startingDayOfWeek is 0 (Sunday), we might want to decide if week starts on Monday or Sunday
    // Provided code assumed week starts on Sunday based on getDayNames order Dim->Sam.
    // However, usually detailed calendars handle this carefully.
    // The original code: Array(startingDayOfWeek).fill(null) implies if starts on Sunday (0), 0 padding. 
    // If starts on Monday (1), 1 padding. 
    // Let's stick to the previous loop logic but with dates.
    // Wait, the previous logic was `Array(startingDayOfWeek).fill(null)`. 
    // if startingDayOfWeek is 2 (Tuesday), we need 2 days: Sunday, Monday.
    // new Date(year, month, 0) is last day of prev month.
    // new Date(year, month, -1) is day before that.

    // Let's redo paddingDaysBefore correctly logic
    const prevMonthDays = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      // if today is Tuesday (2), i goes 0, 1.
      // i=0: day before 1st is 0 (last of prev month) - (startingDayOfWeek - 1 - 0) ?
      // Easier:
      // day 1 is (year, month, 1)
      // previous days are (year, month, 0), (year, month, -1), ...
      // We need 'startingDayOfWeek' number of days.
      // The last one we need is (year, month, 0). 
      // The first one we need is (year, month, 1 - startingDayOfWeek).
      prevMonthDays.push(new Date(year, month, 1 - (startingDayOfWeek - i)));
    }

    // Calculate padding days for end (next month)
    // We want a fixed grid of 6 weeks (42 days) usually to be safe, or just enough to fill the last week.
    // Original: 42 - daysInMonth.length - startingDayOfWeek
    const totalDaysShown = prevMonthDays.length + daysInMonth.length;
    const daysNeededAfter = 42 - totalDaysShown;

    const nextMonthDays = [];
    for (let i = 1; i <= daysNeededAfter; i++) {
      nextMonthDays.push(new Date(year, month + 1, i));
    }

    return [...prevMonthDays, ...daysInMonth, ...nextMonthDays];
  };

  const monthDates = useMemo(() => getMonthDates(), [currentDate]);

  const getEmployeeName = (employeeId) => {
    const employee = effectiveEmployees.find(emp => emp.userId === employeeId);
    return employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Inconnu';
  };

  const appointmentsWithColors = useMemo(() => {
    return effectiveAppointments.map(app => {
      const employee = effectiveEmployees.find(emp => emp.userId === app.employeeId);
      const dateObj = app.date instanceof Timestamp ? app.date.toDate() : new Date(app.date);

      return {
        ...app,
        date: dateObj, // Ensure filtering uses a real Date object
        employeeColor: employee ? employee.color : '#808080',
        employeeName: employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Non assigné',
        start: dateObj
      };
    }).sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [effectiveAppointments, effectiveEmployees]);

  const handleCellClick = (date, employeeId) => {
    if (!canEditAppointments) return;
    setSelectedDate(new Date(date));
    setSelectedEmployee(employeeId);
    setShowAddModal(true);
  };

  const handleAppointmentClick = (appointment) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      return;
    }

    const timeout = setTimeout(() => {
      setSelectedAppointment(appointment);
      setIsViewModalOpen(true);
      setClickTimeout(null);
    }, 300);
    setClickTimeout(timeout);
  };

  const handleAppointmentDoubleClick = (appointment) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }
    setSelectedAppointment(appointment);
    setShowEditModal(true);
  };

  const handleAddAppointment = async (appointmentData) => {
    try {
      const { recurrenceBase, recurrenceFrequency, recurrenceDay, recurrenceMonthDay, recurrenceMonthType, recurrenceMonthWeek, recurrenceMonthWeekday, recurrenceEndType, recurrenceEndAfter, recurrenceEndDate, ...baseAppointmentData } = appointmentData;

      if (appointmentData.isRecurring) {
        const recurringAppointments = generateRecurringAppointments(appointmentData, safeDb);

        const batch = writeBatch(safeDb);

        recurringAppointments.forEach((app) => {
          const docRef = doc(collection(safeDb, 'appointments'));
          // Nettoyer l'objet avant l'ajout
          const cleanApp = {
            ...app,
            clientId: client.id
          };

          // Supprimer les champs qui ne doivent pas être dans Firestore
          delete cleanApp.employeeColor;
          delete cleanApp.employeeName;

          batch.set(docRef, cleanApp);
        });

        await batch.commit();
        if (internalFetchAppointments) internalFetchAppointments();
      } else {
        // Nettoyer les champs de récurrence pour un rendez-vous unique
        const cleanAppointmentData = {
          ...baseAppointmentData,
          clientId: client.id,
          isRecurring: false,
          recurrenceSeriesId: null,
          recurrenceBase: null,
          recurrenceFrequency: null,
          recurrenceDay: null,
          recurrenceMonthDay: null,
          recurrenceMonthType: null,
          recurrenceMonthWeek: null,
          recurrenceMonthWeekday: null,
          recurrenceEndType: null,
          recurrenceEndAfter: null,
          recurrenceEndDate: null
        };
        const docRef = await addDoc(collection(safeDb, 'appointments'), cleanAppointmentData);
        if (setEffectiveAppointments) {
          setEffectiveAppointments([...effectiveAppointments, { ...cleanAppointmentData, id: docRef.id }]);
        }
      }
      setShowAddModal(false);
    } catch (error) {
      console.error("Erreur lors de l'ajout du rendez-vous: ", error);
    }
  };

  const handleUpdateAppointment = async (updatedData, updateSeries = false) => {
    try {
      if (updateSeries && selectedAppointment.recurrenceSeriesId) {
        setPendingAppointmentUpdate({ ...selectedAppointment, ...updatedData });
        setShowRecurrenceModal(true);
      } else {
        await updateDoc(doc(safeDb, 'appointments', selectedAppointment.id), updatedData);
        if (setEffectiveAppointments) {
          setEffectiveAppointments(effectiveAppointments.map(app =>
            app.id === selectedAppointment.id ? { ...app, ...updatedData } : app
          ));
        }
        setShowEditModal(false);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rendez-vous: ", error);
    }
  };

  const handleRecurrenceConfirm = async (action, appointmentToUpdate) => {
    try {
      const q = query(collection(safeDb, 'appointments'), where("recurrenceSeriesId", "==", appointmentToUpdate.recurrenceSeriesId));
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(safeDb);

      const updateData = { ...appointmentToUpdate };
      delete updateData.id;
      delete updateData.date; // Ne pas mettre à jour la date pour la série
      delete updateData.employeeColor;
      delete updateData.employeeName;

      // Supprimer champs undefined
      Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);


      if (action === 'series') {
        querySnapshot.forEach((document) => {
          // On garde la date originale de chaque occurrence
          batch.update(document.ref, updateData);
        });
      } else if (action === 'future') {
        querySnapshot.forEach((document) => {
          const docData = document.data();
          if (docData.date.toDate() >= appointmentToUpdate.date) { // Comparer les dates (objets Date ou Timestamp Firebase)
            batch.update(document.ref, updateData);
          }
        });
      } else { // 'single'
        const docRef = doc(safeDb, 'appointments', appointmentToUpdate.id);
        const singleUpdateData = { ...appointmentToUpdate };
        delete singleUpdateData.id;
        delete singleUpdateData.employeeColor;
        delete singleUpdateData.employeeName;
        // Supprimer champs undefined
        Object.keys(singleUpdateData).forEach(key => singleUpdateData[key] === undefined && delete singleUpdateData[key]);

        batch.update(docRef, singleUpdateData);
      }

      await batch.commit();
      if (internalFetchAppointments) internalFetchAppointments();
      setShowRecurrenceModal(false);
      setShowEditModal(false);
      setPendingAppointmentUpdate(null);
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la récurrence:", error);
    }
  };

  const handleMoveRecurrenceConfirm = async (moveData) => {
    // moveData = { scope: 'this' | 'future' | 'period', endDate: 'YYYY-MM-DD' | null }
    if (!pendingMoveData) return;

    const { appointment, displacementDelta } = pendingMoveData;
    const { scope, endDate } = moveData;

    try {
      const batch = writeBatch(safeDb);

      if (scope === 'this') {
        // Déplacer seulement celui-ci
        const newDate = new Date(appointment.date.getTime() + displacementDelta);
        const docRef = doc(safeDb, 'appointments', appointment.id);
        batch.update(docRef, { date: Timestamp.fromDate(newDate) });
      } else {
        // Récupérer la série
        const q = query(collection(safeDb, 'appointments'), where("recurrenceSeriesId", "==", appointment.recurrenceSeriesId));
        const querySnapshot = await getDocs(q);

        const currentAppDate = appointment.date;

        querySnapshot.forEach(document => {
          const docData = document.data();
          const docDate = docData.date.toDate();

          let shouldUpdate = false;

          if (scope === 'future') {
            if (docDate >= currentAppDate) {
              shouldUpdate = true;
            }
          } else if (scope === 'period') {
            const periodEnd = new Date(endDate);
            periodEnd.setHours(23, 59, 59, 999);
            if (docDate >= currentAppDate && docDate <= periodEnd) {
              shouldUpdate = true;
            }
          }

          if (shouldUpdate) {
            const newDocDate = new Date(docDate.getTime() + displacementDelta);
            batch.update(document.ref, { date: Timestamp.fromDate(newDocDate) });
          }
        });
      }

      await batch.commit();
      if (internalFetchAppointments) internalFetchAppointments();
    } catch (error) {
      console.error("Erreur lors du déplacement de la série:", error);
    } finally {
      setShowMoveModal(false);
      setPendingMoveData(null);
    }
  };


  const handleDeleteAppointment = async (appointmentId, deleteSeries = false) => {
    try {
      if (deleteSeries && selectedAppointment && selectedAppointment.recurrenceSeriesId) {
        // Logique de suppression de série (similaire à handleRecurrenceConfirm mais avec delete)
        // Simplification: suppression de toute la série pour l'instant ou future
        const q = query(collection(safeDb, 'appointments'), where("recurrenceSeriesId", "==", selectedAppointment.recurrenceSeriesId));
        const querySnapshot = await getDocs(q);
        const batch = writeBatch(safeDb);
        querySnapshot.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      } else {
        await deleteDoc(doc(safeDb, 'appointments', appointmentId));
      }
      if (setEffectiveAppointments) {
        setEffectiveAppointments(effectiveAppointments.filter(app => app.id !== appointmentId)); // Optimiste update plus complexe pour série
      }
      if (internalFetchAppointments) internalFetchAppointments(); // Refresh safe
      setShowEditModal(false);
      setIsViewModalOpen(false);
      setSelectedAppointment(null);
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous: ", error);
    }
  };

  const isDateInLeave = (date, employeeId) => {
    return leaves.some(leave => {
      if (leave.userId !== employeeId) return false;
      const start = leave.startDate.toDate();
      const end = leave.endDate.toDate();
      // On compare juste les jours pour simplifier l'affichage mois
      const checkDate = new Date(date).setHours(0, 0, 0, 0);
      return checkDate >= new Date(start).setHours(0, 0, 0, 0) &&
        checkDate <= new Date(end).setHours(0, 0, 0, 0);
    });
  };

  // Helper pour empêcher le drag si on clique sur un champ de formulaire
  const isFormField = (element) => {
    if (element.tagName === 'INPUT' || element.tagName === 'SELECT' || element.tagName === 'TEXTAREA' || element.tagName === 'BUTTON') {
      return true;
    }
    const formFieldTags = ['input', 'select', 'textarea', 'button'];
    return formFieldTags.includes(element.tagName) || element.isContentEditable;
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    const isDraggingWithCtrl = event.activatorEvent?.ctrlKey || event.activatorEvent?.metaKey || copyModeRef.current;
    setIsCopyMode(isDraggingWithCtrl);
    copyModeRef.current = isDraggingWithCtrl;
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;
    if (active.id === over.id) return;

    const appointment = appointmentsWithColors.find(app => app.id === active.id);
    if (!appointment) return;

    const targetData = over.data.current;
    let destinationDate;

    // Résolution de la date cible
    if (targetData?.date) {
      // Cas DroppableCell direct
      destinationDate = targetData.date;
    } else if (targetData?.appointment?.date) {
      // Cas SortableAppointment (on a lâché sur un rendez-vous)
      // On s'assure de récupérer l'objet Date
      const appDate = targetData.appointment.date;
      destinationDate = appDate instanceof Date ? appDate : (appDate?.toDate ? appDate.toDate() : new Date(appDate));
    } else if (targetData?.sortable?.containerId) {
      // Cas SortableContext (via le conteneur)
      const timestamp = parseInt(targetData.sortable.containerId);
      if (!isNaN(timestamp)) {
        destinationDate = new Date(timestamp);
      }
    } else {
      // Fallback sur l'ID (timestamp pour les cellules vides)
      const timestamp = parseInt(over.id);
      if (isNaN(timestamp) || over.id.toString().startsWith('empty-')) return;
      destinationDate = new Date(timestamp);
    }

    if (!destinationDate) return;
    destinationDate.setHours(0, 0, 0, 0); // Sécurité anti-décalage

    // Calculate offset to preserve time
    const oldDate = new Date(appointment.date);
    const startOfDay = new Date(oldDate);
    startOfDay.setHours(0, 0, 0, 0);
    const timeDiff = oldDate.getTime() - startOfDay.getTime();

    const newDate = new Date(destinationDate.getTime() + timeDiff);
    const isCtrlCurrentlyPressed = copyModeRef.current;

    try {
      if (isCtrlCurrentlyPressed) {
        // Copy logic
        const newAppointment = {
          ...appointment,
          date: Timestamp.fromDate(newDate),
          clientId: client.id,
          isRecurring: false,
          recurrenceSeriesId: null,
          status: appointment.employeeId ? 'assigne' : 'libre'
        };

        // Clean up properties that shouldn't be copied
        const propsToRemove = ['id', 'employeeColor', 'employeeName', 'recurrenceBase', 'recurrenceFrequency', 'recurrenceDay', 'recurrenceMonthDay', 'recurrenceMonthType', 'recurrenceMonthWeek', 'recurrenceMonthWeekday', 'recurrenceEndType', 'recurrenceEndAfter', 'recurrenceEndDate'];
        propsToRemove.forEach(prop => delete newAppointment[prop]);

        const docRef = await addDoc(collection(safeDb, 'appointments'), newAppointment);
        if (internalFetchAppointments) internalFetchAppointments();
      } else {
        // Move Logic
        if (appointment.isRecurring && appointment.recurrenceSeriesId) {
          const displacementDelta = newDate.getTime() - oldDate.getTime();
          setPendingMoveData({
            appointment,
            displacementDelta
          });
          setShowMoveModal(true);
        } else {
          await updateDoc(doc(safeDb, 'appointments', appointment.id), {
            date: Timestamp.fromDate(newDate)
          });
          if (setEffectiveAppointments) {
            setEffectiveAppointments(prev => prev.map(app =>
              app.id === appointment.id ? { ...app, date: newDate } : app
            ));
          }
        }
      }
    } catch (error) {
      console.error("Erreur lors de la manipulation du rendez-vous:", error);
    }

    setIsCopyMode(false);
    copyModeRef.current = false;
  }, [appointmentsWithColors, safeDb, client.id, internalFetchAppointments, setEffectiveAppointments]);

  // Fonction utilitaire pour vérifier si une couleur est claire
  const isLightColor = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return brightness > 155;
  };

  const getAppointmentStatusClass = (appointment) => {
    switch (appointment.status) {
      case 'libre': return 'bg-red-100 dark:bg-red-900/30 border-red-500 text-red-800 dark:text-red-300';
      case 'reserve': return 'bg-amber-100 dark:bg-amber-900/30 border-amber-500 text-amber-800 dark:text-amber-300';
      case 'assigne': return ''; // Handle dynamically
      case 'in_progress': return 'bg-orange-100 dark:bg-orange-900/30 border-orange-500 text-orange-800 dark:text-orange-300';
      case 'completed': return 'bg-gray-100 dark:bg-gray-700 border-gray-400 dark:border-gray-500 text-gray-600 dark:text-gray-300 opacity-80';
      default: return '';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'libre': return <AlertTriangle size={12} className="inline-block mr-1" />;
      case 'reserve': return <Clock size={12} className="inline-block mr-1 animate-spin" />;
      case 'assigne': return <User size={12} className="inline-block mr-1" />;
      case 'in_progress': return <Clock size={12} className="inline-block mr-1" />;
      case 'completed': return <Check size={12} className="inline-block mr-1" />;
      default: return null;
    }
  };

  const getStatusText = (appointment) => {
    switch (appointment.status) {
      case 'libre': return 'Plage à combler';
      case 'reserve': return 'En attente de validation';
      case 'assigne': return appointment.employeeName || getEmployeeName(appointment.employeeId);
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      default: return 'Statut inconnu';
    }
  };

  const SortableAppointment = ({
    appointment,
    statusClass,
    icon,
    statusText,
    dynamicStyle,
    isCtrlPressed,
    onClick,
    onDoubleClick
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: appointment.id,
      data: {
        appointment: {
          ...appointment,
          date: appointment.date instanceof Date ? appointment.date : (appointment.date?.toDate ? appointment.date.toDate() : new Date(appointment.date))
        }
      }
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 1000 : 1,
      ...dynamicStyle
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`
          px-1 py-0.5 rounded text-[11px] mb-0.5 cursor-pointer border-l-4 relative shadow-sm hover:shadow hover:-translate-y-px hover:brightness-95 hover:z-10
          ${statusClass}
          ${isDragging ? 'opacity-50 z-[1000]' : ''}
        `}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        {appointment.notes && appointment.notes.content && (
          <div className="absolute top-0.5 right-0.5 text-[10px]">📝</div>
        )}
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center justify-between font-medium whitespace-nowrap overflow-hidden text-ellipsis">
            <span className="flex items-center gap-0.5">{icon}</span>
            <span className="truncate">{statusText}</span>
          </div>
          <div className="text-[10px] opacity-90">
            <Clock className="inline-block mr-0.5" size={10} />
            {`${appointment.startTime} - ${appointment.endTime}`}
          </div>
        </div>
        {isCtrlPressed && isDragging && (
          <div className="absolute top-0 right-0 p-1 bg-blue-500 rounded-full text-white shadow-md">
            <Copy size={12} className="" />
          </div>
        )}
      </div>
    );
  };

  const DroppableCell = ({ date, children, isCurrentMonth, isSameDayAsToday, onCellClick }) => {
    // date is now always guaranteed
    const dateKey = date.getTime().toString();

    const { setNodeRef, isOver } = useDroppable({
      id: dateKey,
      data: { date },
    });

    return (
      <div
        ref={setNodeRef}
        className={`
          flex flex-col min-h-[120px] p-1 relative transition-all duration-200 border-r border-b border-gray-200 dark:border-gray-700
          ${isCurrentMonth ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500'}
          ${isOver ? 'bg-blue-50 dark:bg-blue-500/20 ring-2 ring-blue-500 ring-inset z-10' : ''}
          hover:bg-gray-50 dark:hover:bg-gray-800
          ${canEditAppointments ? 'cursor-pointer' : ''}
        `}
        onClick={() => onCellClick(date)}
      >
        <div className={`
          text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full
          ${isSameDayAsToday
            ? 'bg-blue-600 text-white'
            : 'text-gray-700 dark:text-gray-300'}
        `}>
          {date.getDate()}
        </div>
        {children}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 select-none">
      <div className="mb-2">
        <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
          {getDayNames().map((day, index) => (
            <div key={index} className="p-2 text-center font-semibold text-gray-600 dark:text-gray-400 text-sm bg-gray-100 dark:bg-gray-800">
              {day}
            </div>
          ))}
        </div>
      </div>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        collisionDetection={closestCorners}
      >
        <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 flex-1 overflow-y-auto min-h-0 border-l border-t border-gray-200 dark:border-gray-700">
          {monthDates.map((date, index) => (
            <DroppableCell
              key={date.getTime()}
              date={date}
              isCurrentMonth={date.getMonth() === currentDate.getMonth()}
              isSameDayAsToday={isSameDay(date, new Date())}
              onCellClick={handleCellClick}
            >
              {date && (
                <SortableContext
                  id={date.getTime().toString()}
                  items={appointmentsWithColors.filter(app => isSameDay(app.date, date)).map(app => app.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {/* Ajout de l'indicateur de congés */}
                  {employees.map(employee => {
                    if (isDateInLeave(date, employee.userId)) {
                      return (
                        <div
                          key={`employee-offday-${employee.userId}`}
                          className="bg-red-50 dark:bg-red-900/20 border border-dashed border-red-300 dark:border-red-700/50 rounded px-1 py-0.5 mb-0.5 text-[11px] text-red-700 dark:text-red-300"
                        >
                          <div className="flex items-center gap-1">
                            <Clock size={12} className="text-red-400" />
                            <span className="truncate">
                              {employee.firstName} absent
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}

                  {/* Indicateur de compteur pour les jours avec beaucoup de rendez-vous */}
                  {appointmentsWithColors.filter(app => isSameDay(app.date, date)).length > maxVisibleAppointments && (
                    <div className="absolute top-1 right-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] px-1 rounded-full">
                      {appointmentsWithColors.filter(app => isSameDay(app.date, date)).length}
                    </div>
                  )}

                  <div className="flex flex-col gap-0.5 flex-1 w-full">
                    {appointmentsWithColors
                      .filter(app => isSameDay(app.date, date))
                      .slice(0, expandedDays.has(date.getTime().toString()) ? undefined : maxVisibleAppointments)
                      .map((appointment, appIndex) => {
                        let dynamicStyle = {};
                        if (appointment.status === 'assigne') {
                          dynamicStyle = {
                            backgroundColor: appointment.employeeColor || '#808080',
                            borderLeftColor: appointment.employeeColor || '#808080',
                            color: isLightColor(appointment.employeeColor || '#808080') ? '#1F2937' : '#F9FAFB'
                          };
                        }

                        return (
                          <SortableAppointment
                            key={appointment.id}
                            appointment={appointment}
                            statusClass={getAppointmentStatusClass(appointment)}
                            icon={getStatusIcon(appointment.status)}
                            statusText={getStatusText(appointment)}
                            isCtrlPressed={isCopyMode}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAppointmentClick(appointment);
                            }}
                            onDoubleClick={(e) => {
                              handleAppointmentDoubleClick(appointment);
                            }}
                            dynamicStyle={dynamicStyle}
                          />
                        );
                      })}

                    {/* Bouton "Voir plus" pour les jours avec beaucoup de rendez-vous */}
                    {appointmentsWithColors.filter(app => isSameDay(app.date, date)).length > maxVisibleAppointments && (
                      <div
                        className="text-[11px] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 cursor-pointer text-center mt-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDayExpansion(date);
                        }}
                      >
                        {expandedDays.has(date.getTime().toString())
                          ? `Voir moins (-${appointmentsWithColors.filter(app => isSameDay(app.date, date)).length - maxVisibleAppointments})`
                          : `Voir +${appointmentsWithColors.filter(app => isSameDay(app.date, date)).length - maxVisibleAppointments} plus`
                        }
                      </div>
                    )}
                  </div>
                </SortableContext>
              )}
            </DroppableCell>
          ))}
        </div>
        {createPortal(
          <DragOverlay zIndex={1000} dropAnimation={null}>
            {activeId ? (() => {
              const app = appointmentsWithColors.find(a => a.id === activeId);
              if (!app) return null;
              const dynamicStyle = app.status === 'assigne' ? {
                backgroundColor: app.employeeColor || '#808080',
                borderLeftColor: app.employeeColor || '#808080',
                color: isLightColor(app.employeeColor || '#808080') ? '#1F2937' : '#F9FAFB'
              } : {};

              return (
                <div className={`
                  px-1 py-0.5 rounded text-[11px] mb-0.5 border-l-4 relative shadow-sm z-[1000] pointer-events-none
                  ${getAppointmentStatusClass(app)}
                `} style={{
                    opacity: 0.9,
                    transform: 'scale(1.05)',
                    ...dynamicStyle,
                    backgroundColor: isCopyMode ? '#10B981' : dynamicStyle.backgroundColor,
                    borderColor: isCopyMode ? '#059669' : dynamicStyle.borderColor,
                  }}>
                  {app.notes && app.notes.content && (
                    <div className="absolute top-0.5 right-0.5 text-[10px]">📝</div>
                  )}
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center justify-between font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                      <span className="flex items-center gap-0.5">{getStatusIcon(app.status)}</span>
                      <span className="truncate">{getStatusText(app)}</span>
                    </div>
                    {app.startTime && (
                      <div className="text-[10px] opacity-90">
                        <Clock className="inline-block mr-0.5" size={10} />
                        {`${app.startTime} - ${app.endTime}`}
                      </div>
                    )}
                  </div>
                  {isCopyMode && (
                    <div className="mt-1 px-1 py-0.5 bg-green-600 text-white rounded text-[9px] font-bold flex items-center gap-1 shadow-sm">
                      <Copy size={8} /> Copie
                    </div>
                  )}
                </div>
              );
            })() : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>

      {showAddModal && (
        <ClientAddAppointmentModal
          date={selectedDate}
          employees={effectiveEmployees}
          onSave={handleAddAppointment}
          onClose={() => {
            setShowAddModal(false);
            setSelectedDate(new Date());
          }}
          initialEmployeeId={selectedEmployee}
        />
      )}

      {isViewModalOpen && selectedAppointment && (
        <AppointmentViewModal
          appointment={selectedAppointment}
          employees={employees}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedAppointment(null);
          }}
          onEdit={() => {
            setIsViewModalOpen(false);
            setShowEditModal(true);
          }}
          onDelete={handleDeleteAppointment}
          hasPermission={hasPermission}
        />
      )}

      {showEditModal && selectedAppointment && (
        <ClientEditAppointmentModal
          appointment={selectedAppointment}
          employees={employees}
          onSave={handleUpdateAppointment}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAppointment(null);
          }}
          onDelete={handleDeleteAppointment}
          canEditAppointments={canEditAppointments}
        />
      )}

      {showRecurrenceModal && pendingAppointmentUpdate && (
        <RecurrenceEditModal
          isOpen={showRecurrenceModal}
          onClose={() => setShowRecurrenceModal(false)}
          onConfirm={handleRecurrenceConfirm}
          appointment={pendingAppointmentUpdate}
        />
      )}

      {showMoveModal && pendingMoveData && (
        <RecurrenceMoveModal
          isOpen={showMoveModal}
          onClose={() => {
            setShowMoveModal(false);
            setPendingMoveData(null);
          }}
          onConfirm={handleMoveRecurrenceConfirm}
          appointment={pendingMoveData.appointment}
          actionType="move"
        />
      )}
    </div>
  );
};

// Ajouter le displayName pour le débogage
ClientMonthView.displayName = 'ClientMonthView';

export default ClientMonthView;
